import { Order, OrderGroup } from 'entities/orders/model/types';
import { formatWithLocale } from 'shared/lib/datepicker';
import {
	extractDate,
	formatDateToRelativeDate,
	formatTime,
	FormatTimeObject,
} from 'shared/lib/time';

export const groupOrdersByDate = (
	ordersList: Order[],
	state: { ordersGroups: OrderGroup[] },
) => {
	const newOrdersMap = new Map<string, Map<string, Order[]>>();
	const newOrdersGroups: OrderGroup[] = [];
	const ordersGroupsLength = state.ordersGroups.length;
	const lastOrderTable =
		ordersGroupsLength > 0
			? state.ordersGroups[ordersGroupsLength - 1]
			: null;
	const ordersMap = new Map<string, Order[]>();

	ordersList.forEach(order => {
		const [date] = extractDate(order.dt_start);
		const formattedTime = formatTime({
			time: order.dt_start,
			byUtc: true,
		}) as FormatTimeObject;

		const orderDate = formatWithLocale(new Date(date), 'dd MMMM yyyy');
		const orderTime = formattedTime.time;

		if (
			state.ordersGroups.length > 1 &&
			lastOrderTable?.date === orderDate
		) {
			const byTime = ordersMap.get(orderTime);
			if (byTime) {
				byTime.push({ ...order });
			} else {
				ordersMap.set(orderTime, [{ ...order }]);
			}
			return;
		}

		if (!newOrdersMap.has(orderDate)) {
			newOrdersMap.set(orderDate, new Map());
		}

		const timeMap = newOrdersMap.get(orderDate)!;

		if (!timeMap.has(orderTime)) {
			timeMap.set(orderTime, []);
		}

		timeMap.get(orderTime)!.push({ ...order });
	});

	if (lastOrderTable) {
		lastOrderTable.orders.forEach(orderTable => {
			const list = orderTable.ordersList;
			const ordersByTime = ordersMap.get(orderTable.time);
			if (ordersByTime) {
				const filtredOrders = ordersByTime.filter(order => {
					if (list.includes(order)) {
						return order;
					}
				});

				if (filtredOrders.length) {
					orderTable.ordersList = list.concat(filtredOrders);
					ordersMap.delete(orderTable.time);
				}
			}
		});

		Object.entries(ordersMap).forEach(([time, ordersList]) => {
			lastOrderTable.orders.push({ time, ordersList });
		});
	}

	newOrdersMap.forEach((timeMap, date) => {
		newOrdersGroups.push({
			date,
			orders: Array.from(timeMap.entries()).map(([time, ordersList]) => ({
				time,
				ordersList,
			})),
		});
	});

	return newOrdersGroups;
};

export const groupOrdersByDay = (ordersList: Order[]) => {
	const ordersMap = new Map<string, Order[]>();

	ordersList.forEach(order => {
		const [date] = extractDate(order.dt_start);

		const label = formatDateToRelativeDate(date);

		if (ordersMap.has(label)) {
			const orders = ordersMap.get(label)!;
			orders.push(order);
			ordersMap.set(label, orders);
		} else {
			ordersMap.set(label, [order]);
		}
	});

	const result = Array.from(ordersMap).map(([date, orders]) => ({
		date,
		orders,
	}));

	return result;
};
